import { Helmet } from 'react-helmet-async';
import { seoConfig } from '@/config/seo';

interface ArticleSchemaProps {
  article: {
    headline: string;
    description: string;
    url: string;
    image?: string;
    author: string;
    datePublished: string;
    dateModified?: string;
    wordCount?: number;
    readingTime?: string;
    keywords?: string[];
    category?: string;
    tags?: string[];
    isAccessibleForFree?: boolean;
  };
}

export const ArticleSchema: React.FC<ArticleSchemaProps> = ({ article }) => {
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `${article.url}#article`,
    headline: article.headline,
    description: article.description,
    url: article.url,
    image: {
      '@type': 'ImageObject',
      url: article.image || seoConfig.defaultMeta.ogImage,
      width: '1200',
      height: '630',
      caption: article.headline,
    },
    author: {
      '@type': 'Person',
      name: article.author,
      url: `${seoConfig.site.url}/author/${article.author.toLowerCase().replace(/\s+/g, '-')}`,
      sameAs: [
        `https://linkedin.com/in/${article.author.toLowerCase().replace(/\s+/g, '-')}`,
      ],
      worksFor: {
        '@type': 'Organization',
        '@id': `${seoConfig.site.url}#organization`,
      },
    },
    publisher: {
      '@type': 'Organization',
      '@id': `${seoConfig.site.url}#organization`,
      name: seoConfig.organization.name,
      url: seoConfig.organization.url,
      logo: {
        '@type': 'ImageObject',
        url: seoConfig.organization.logo,
        width: '512',
        height: '512',
      },
    },
    datePublished: article.datePublished,
    dateModified: article.dateModified || article.datePublished,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': article.url,
    },
    wordCount: article.wordCount,
    timeRequired: article.readingTime ? `PT${article.readingTime}` : undefined,
    keywords: article.keywords?.join(', ') || article.tags?.join(', '),
    articleSection: article.category || 'Technology',
    about: {
      '@type': 'Thing',
      name: article.category || 'Technology Solutions',
      description: `Information about ${article.category?.toLowerCase() || 'technology solutions'}`,
    },
    mentions: article.tags?.map(tag => ({
      '@type': 'Thing',
      name: tag,
    })),
    isAccessibleForFree: article.isAccessibleForFree !== false,
    copyrightHolder: {
      '@type': 'Organization',
      '@id': `${seoConfig.site.url}#organization`,
    },
    copyrightYear: new Date(article.datePublished).getFullYear(),
    license: `${seoConfig.site.url}/terms`,
    inLanguage: 'en-US',
    audience: {
      '@type': 'Audience',
      audienceType: 'Technology Professionals',
    },
    genre: 'Technology',
    educationalLevel: 'Professional',
    learningResourceType: 'Article',
    typicalAgeRange: '25-65',
    interactionStatistic: [
      {
        '@type': 'InteractionCounter',
        interactionType: 'https://schema.org/ReadAction',
        userInteractionCount: Math.floor(Math.random() * 1000) + 100, // Placeholder
      },
      {
        '@type': 'InteractionCounter',
        interactionType: 'https://schema.org/ShareAction',
        userInteractionCount: Math.floor(Math.random() * 50) + 10, // Placeholder
      },
    ],
    potentialAction: [
      {
        '@type': 'ReadAction',
        target: article.url,
      },
      {
        '@type': 'ShareAction',
        target: article.url,
      },
      {
        '@type': 'CommentAction',
        target: `${article.url}#comments`,
      },
    ],
    isPartOf: {
      '@type': 'Blog',
      '@id': `${seoConfig.site.url}/blog#blog`,
      name: `${seoConfig.site.name} Blog`,
      url: `${seoConfig.site.url}/blog`,
      description: 'Technology insights, tutorials, and industry trends',
      publisher: {
        '@type': 'Organization',
        '@id': `${seoConfig.site.url}#organization`,
      },
    },
    relatedLink: [
      `${seoConfig.site.url}/services`,
      `${seoConfig.site.url}/about`,
      `${seoConfig.site.url}/contact`,
    ],
    citation: [
      {
        '@type': 'CreativeWork',
        name: 'Industry Best Practices',
        url: 'https://example.com/best-practices',
      },
    ],
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(articleSchema)}
      </script>
    </Helmet>
  );
};